import lightFormat from "date-fns/lightFormat";
import getYear from "date-fns/getYear";
import { palette } from "lib/miolo";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import orderBy from "lodash/orderBy";
import classNames from "components/ui/classNames";
import type { CSSProperties } from "react";

interface IOfficersProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

export default function Officers({ issuer }: IOfficersProps) {
  const sortedOfficers = orderBy(issuer.officers, ["board", "name"], ["desc", "asc"]);

  const officers = [
    ...sortedOfficers.filter((officer) => {
      const isCEO = issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name;
      return isCEO;
    }),
    ...sortedOfficers.filter((officer) => {
      const isCEO = issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name;
      return !isCEO;
    }),
  ];

  return (
    <div className="overflow-hidden rounded bg-white p-2">
      <table className="min-w-full border-collapse border-inherit text-left indent-0 text-sm tracking-wide">
        <thead className="bg-white">
          <tr>
            <th className="px-4 py-3 font-semibold uppercase text-neutral-700">Nome</th>
            <th className="px-4 py-3 font-semibold uppercase text-neutral-700">Quadro</th>
            <th className="px-4 py-3 font-semibold uppercase text-neutral-700">Mandato</th>
          </tr>
        </thead>

        <tbody>
          {officers.map((officer, index) => {
            const yearOfBirth = getYear(parseDateYmd(officer.dateOfBirth));
            const age = getYear(new Date()) - yearOfBirth;

            return (
              <tr
                key={`${officer.name}/${String(index)}`}
                className={classNames(
                  "group border-t border-neutral-200",
                  "hover:border-t-transparent [&:hover+tr]:border-t-transparent",
                )}
              >
                <td
                  className={classNames(
                    "px-4 py-3 group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <div className="flex items-center">
                    <div>
                      <div>
                        <span className="text-base font-medium text-slate-600">{officer.name}</span>
                      </div>

                      <div>
                        <span className="text-sm tracking-wider text-slate-500">
                          {officer.position} • {age} anos
                        </span>
                      </div>
                    </div>

                    {issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name && (
                      <div className="ml-3">
                        <span className="rounded-xl bg-indigo-100 px-3 py-1 text-xs font-bold tracking-wider text-blue-700">
                          CEO
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td
                  className={classNames(
                    "px-4 py-3 group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <span
                    className="rounded-xl px-3 py-1 font-medium tracking-wider"
                    style={getBoardTagCSS(officer.board)}
                  >
                    {getCleanBoard(officer.board)}
                  </span>
                </td>
                <td
                  className={classNames(
                    "px-4 py-3 group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <div className="max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap">
                    <span title={officer.termOfOffice} className="text-base font-medium text-slate-600">
                      {officer.termOfOffice}
                    </span>
                  </div>

                  <div>
                    <span className="text-sm tracking-wider text-slate-500">
                      {"Eleito em "}
                      <time dateTime={officer.electedDate}>
                        {lightFormat(parseDateYmd(officer.electedDate), "dd/MM/yyyy")}
                      </time>
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getBoardTagCSS(board: string): CSSProperties {
  if (board === "Pertence apenas à Diretoria") {
    return {
      backgroundColor: palette.grannyApple,
      color: palette.stromboli,
    };
  }
  if (board === "Pertence apenas ao Conselho de Administração") {
    return {
      backgroundColor: palette.selago,
      color: palette.royalBlue,
    };
  }
  return {
    backgroundColor: palette.catskillWhite,
    color: palette.blueBayoux,
  };
}

function getCleanBoard(board: string) {
  if (board === "Pertence apenas à Diretoria") {
    return "Diretoria";
  }
  if (board === "Pertence apenas ao Conselho de Administração") {
    return "Conselho de Administração";
  }
  return "Conselho Fiscal";
}
